import type { DesignDocument, DesignModel, DesignModelSlug, DesignProfile, DesignSectionSlug } from '@forgd/contract/types'
import { useChosenModel } from '#design-tokenomics/composables/useChosenModel'
import { useDesignDraft } from '#design-tokenomics/composables/useDesignDraft'
import { useDesignTokenomicsApi } from '#design-tokenomics/composables/useDesignTokenomicsApi'
import { usePermissionsDialogStore } from '#auth/composables/usePermissionsDialogStore'
import { useQuery } from '@pinia/colada'

export function useDesignDocument() {
  const route = useRoute()
  const auth = useAuth()
  const api = useDesignTokenomicsApi()
  const draft = useDesignDraft()
  const chosenModel = useChosenModel()
  const toast = useAppToast()
  const permissionsDialog = usePermissionsDialogStore()

  const projectId = computed(() => (route.params.projectId as string | undefined) ?? null)

  const chosenSlug = computed<DesignModelSlug | null>({
    get: () => chosenModel.slug.value ?? draft.document.value?.model.slug ?? null,
    set: next => chosenModel.set(next),
  })

  const demoSlug = computed(() => (projectId.value ? null : chosenSlug.value))

  const seedQuery = useQuery({
    key: () => ['design-tokenomics', 'document', projectId.value ?? 'demo', demoSlug.value ?? 'initial'],
    query: async (): Promise<DesignDocument> => {
      if (projectId.value) {
        return api.fetchDocument(projectId.value)
      }

      const current = await api.fetchInitialDocument()

      if (!demoSlug.value) {
        return current
      }

      return { ...current, model: await api.fetchModel(demoSlug.value) }
    },
    staleTime: Number.POSITIVE_INFINITY,
  })

  const seed = computed(() => seedQuery.data.value ?? null)

  const document = computed(() =>
    projectId.value ? seed.value : (draft.document.value ?? seed.value),
  )

  const model = computed(() => document.value?.model ?? null)
  const profile = computed(() => document.value?.profile ?? null)
  const project = computed(() => document.value?.project ?? null)

  const ticker = computed(() => auth.project?.ticker ?? project.value?.ticker ?? null)

  const slug = computed<DesignModelSlug | null>(() => model.value?.slug ?? demoSlug.value)

  const templateQuery = useQuery({
    key: () => ['design-tokenomics', 'model', slug.value ?? 'none'],
    query: () => api.fetchModel(slug.value!),
    enabled: () => slug.value !== null,
    staleTime: Number.POSITIVE_INFINITY,
  })

  const seedModel = computed(() => templateQuery.data.value ?? null)

  const isCustomized = draft.hasDraft
  const isLoading = computed(() => !document.value && seedQuery.isLoading.value)
  const isSaving = ref(false)

  async function update(next: DesignDocument) {
    if (!projectId.value) {
      const stored = draft.save(next)

      if (stored.ok) {
        toast.success({ title: 'Your changes have been saved successfully' })
      }
      else {
        toast.error({
          title: 'We could not save your changes',
          description: 'This design is too large to keep in your browser. Try removing some detail.',
        })
      }

      return stored
    }

    isSaving.value = true
    try {
      await api.applyDocument(projectId.value, next)
      await seedQuery.refetch()
      toast.success({ title: 'Your changes have been saved successfully' })
      return { ok: true } as const
    }
    catch (error) {
      if (error instanceof Error && 'statusCode' in error && error.statusCode === 403 && !auth.isOrganizationOwner) {
        permissionsDialog.showForbiddenTeammateAction()
      }
      else {
        toast.error({
          title: 'We could not save your changes',
          description: 'Please try again in a moment.',
        })
      }
      return { ok: false, reason: 'request' } as const
    }
    finally {
      isSaving.value = false
    }
  }

  function updateModel(next: DesignModel) {
    if (!document.value) {
      return Promise.resolve({ ok: false, reason: 'malformed' } as const)
    }

    return update({ ...document.value, model: next })
  }

  function updateProfile(changes: Partial<DesignProfile>) {
    if (!document.value) {
      return Promise.resolve({ ok: false, reason: 'malformed' } as const)
    }

    return update({ ...document.value, profile: { ...document.value.profile, ...changes } })
  }

  function setCompleted(section: DesignSectionSlug, completed: boolean) {
    if (!document.value) {
      return Promise.resolve({ ok: false, reason: 'malformed' } as const)
    }

    const rest = document.value.completed.filter(slug => slug !== section)

    return update({ ...document.value, completed: completed ? [...rest, section] : rest })
  }

  function isCompleted(section: DesignSectionSlug) {
    return Boolean(document.value?.completed.includes(section))
  }

  /**
   * Switches models. This is destructive by design - the modal warns that changing a model
   * overrides previous progress - so the draft goes with it.
   */
  async function changeModel(next: DesignModelSlug) {
    // Read before anything is cleared: switching re-keys the seed query, so `document` is briefly
    // empty while the new model loads.
    const base = document.value

    draft.clear()

    if (projectId.value) {
      chosenSlug.value = null

      if (base) {
        await update({ ...base, model: await api.fetchModel(next) })
      }

      return
    }

    chosenSlug.value = next
    await seedQuery.refetch()
  }

  async function setPublished(published: boolean) {
    if (!projectId.value) {
      return { ok: false, reason: 'no-project' } as const
    }

    isSaving.value = true

    try {
      await api.setPublished(projectId.value, published)
      await seedQuery.refetch()

      toast.success({
        title: published
          ? 'Token Public Page published successfully!'
          : 'Token Public Page successfully unpublished!',
      })

      return { ok: true } as const
    }
    catch (error) {
      if (error instanceof Error && 'statusCode' in error && error.statusCode === 403 && !auth.isOrganizationOwner) {
        permissionsDialog.showForbiddenTeammateAction()
      }
      else {
        toast.error({
          title: published ? 'The page could not be published' : 'The page could not be unpublished',
          description: 'Please try again in a moment.',
        })
      }

      return { ok: false, reason: 'request' } as const
    }
    finally {
      isSaving.value = false
    }
  }

  /** Back to the untouched design, keeping the visitor on the same model. */
  function discardChanges() {
    draft.clear()
  }

  return {
    document,
    model,
    seed: seedModel,
    profile,
    project,
    projectId,
    ticker,
    slug,
    isCustomized,
    isLoading,
    isSaving,
    lastEditedAt: draft.updatedAt,
    error: seedQuery.error,
    refresh: seedQuery.refetch,
    update,
    updateModel,
    updateProfile,
    setCompleted,
    isCompleted,
    setPublished,
    changeModel,
    discardChanges,
  }
}
